


import { CheckCircleOutlined } from "@ant-design/icons";
import { CheckAdminType } from "./checkPermission";



/**
 * ฟังก์ชันสำหรับดึงเฉพาะโหนดแม่ (โหนดที่มีลูก) จากข้อมูลต้นฉบับที่ต้องการจัดกลุ่มเป็น Tree
 * @param {Array} treeData - ข้อมูลต้นฉบับที่ต้องการดึงเฉพาะโหนดแม่ (โหนดที่มีลูก) มาเก็บเป็น keys
 * @returns {Array} - Array ของ keys ที่เป็นโหนดแม่ (โหนดที่มีลูก) เท่านั้น
 */
export const getAllHeaderKeys = (treeData) => {
    let keys = [];
    const getKeys = (nodes) => {
        nodes.forEach(node => {
            if (node.children) {
                keys.push(node.key); // เก็บเฉพาะโหนดที่มีลูก (โหนดแม่)
                getKeys(node.children);
            }
        });
    };
    getKeys(treeData);
    return keys;
};

/**
 * ฟังก์ชันสำหรับดึงเฉพาะโหนดแม่ (โหนดที่มีลูก) จากข้อมูลต้นฉบับที่ต้องการจัดกลุ่มเป็น Tree โดยเฉพาะโหนดที่เกี่ยวข้องกับ Resource และ Action Group
 * @param {Array} permissionData - permissionData ที่ต้องการดึงเฉพาะโหนดแม่ (โหนดที่มีลูก) มาเก็บเป็น keys
 * @returns {Array} - Array ของ keys ที่เป็นโหนดแม่ (โหนดที่มีลูก) เท่านั้น โดยเฉพาะโหนดที่เกี่ยวข้องกับ Resource และ Action Group
 */
export const getAllHeaderKeysAndGroupKeys = (permissionData) => {
    if (!Array.isArray(permissionData)) return [];

    const resources = [...new Set(permissionData
        .filter(item => item.ispublic === true)
        .map(item => item.resource))];

    const resourceKeys = resources.map(res => `resource-${res}`);
    const actionGroupKeys = resources.map(res => `action-group-${res}`);

    return [...resourceKeys, ...actionGroupKeys];
};

/**
 * ฟังก์ชันสำหรับดึงเฉพาะโหนดแม่ (โหนดที่มีลูก) จากข้อมูลต้นฉบับที่ต้องการจัดกลุ่มเป็น Tree โดยเฉพาะโหนดที่เกี่ยวข้องกับ Resource และ Action Group
 * @param {Array} permissionData - permissionData ที่ต้องการดึงเฉพาะโหนดแม่ (โหนดที่มีลูก) มาเก็บเป็น keys
 * @returns {Array} - Array ของ keys ที่เป็นโหนดแม่ (โหนดที่มีลูก) เท่านั้น โดยเฉพาะโหนดที่เกี่ยวข้องกับ Resource และ Action Group
 */
export const getAllDisplayablePermissionIds = (permissionData) => {
    let arrData = Array.isArray(permissionData) ? permissionData : [];
    let filterPermiss = arrData.filter((item) => item.ispublic === true);
    const allIds = [];

    if (filterPermiss.length > 0) {
        filterPermiss.forEach((item) => {
            allIds.push(item.id);
        });
    }
    return allIds.map((id) => parseInt(id));
};

/**
 * ฟังก์ชันสำหรับดึงเฉพาะโหนดแม่ (โหนดที่มีลูก) จากข้อมูลต้นฉบับที่ต้องการจัดกลุ่มเป็น Tree โดยเฉพาะโหนดที่เกี่ยวข้องกับ Resource และ Action Group
 * @param {Array} permissions - permissions ที่ต้องการดึงเฉพาะโหนดแม่ (โหนดที่มีลูก) มาเก็บเป็น keys
 * @returns {Object} - Object ที่มี key เป็น permissionId และ value เป็น boolean ที่บอกว่า permission นั้นถูกติ๊กหรือไม่ (isPublic === 1)
 */
export const mapPermissionsToCheckedState = (permissions = []) => {
    const checkedState = {};
    permissions.forEach((p) => {
        if (p && p.permissionId !== undefined) {
            checkedState[p.permissionId] = p.isPublic === 1;
        }
    });
    return checkedState;
};


/**
 * ฟังก์ชันสำหรับ Filter และจัดกลุ่มข้อมูลเข้า Tree
 *
 * @param {Array} data - permissions ที่จะถูกกรองและจัดกลุ่ม
 * @param {any} t - ฟังก์ชันสำหรับแปลข้อความ (เช่น i18n.t) เพื่อแปลคำอธิบายและชื่อ Resource
 * @returns {Array} - ข้อมูลที่ถูกจัดกลุ่มและแปลงเป็นรูปแบบที่ Tree ของ Ant Design ต้องการ
 */
export const getViewTreeData = (data, t) => {
    if (!data || data.length === 0) return [];

    // จัดกลุ่มตาม resource
    const groups = data.reduce((acc, item) => {
        if (item.ispublic === true && item.isPublic === 1 && item.attributes === "*") {
            if (!acc[item.resource]) {
                acc[item.resource] = [];
            }
            acc[item.resource].push(item);
        }
        return acc;
    }, {});

    return Object.keys(groups).map((resource) => {
        const items = groups[resource];
        const normalItems = items.filter(i => i.action !== "action");
        const actionItems = items.filter(i => i.action === "action");

        // สร้างลูกของ Node (Children)
        const children = [
            ...normalItems.map(item => ({
                title: <span style={{ cursor: "no-drop", fontSize: 12.9 }}> {t(item.description) || item.description}</span>,
                key: item.id,
                icon: <CheckCircleOutlined style={{ cursor: "no-drop", color: '#28a745', marginRight: 5, fontSize: 15 }} />, // ไอคอนลูกศรสำหรับ Node ที่มีลูก
                checkable: false, // ป้องกันการติ๊กเล่นตาม logic เดิมของคุณ
            })),
        ];

        // ถ้ามีการกระทำ (Action) ให้สร้าง Sub-node
        if (actionItems.length > 0) {
            children.push({
                title: <span className="font-bold text-blue-600">{t("action")}</span>,
                key: `action-group-${resource}`,
                checkable: false, // ไม่มีช่อง Checkbox หน้าคำว่า action
                selectable: false,
                children: actionItems.map(item => ({
                    title: <span style={{ cursor: "no-drop",  fontSize: 12.9 }}> {t(item.description) || item.description}</span>,
                    key: item.id,
                    checkable: false, // แสดง Checkbox แต่ห้ามแก้ไข (เหมือนต้นฉบับ cursor: no-drop)
                    icon: <CheckCircleOutlined style={{ color: '#28a745', marginRight: 5, fontSize: 15 }} />, // ไอคอนลูกศรสำหรับ Node ที่มีลูก
                    // ไม่ต้องใส่ checked ตรงนี้
                })),
            });
        }

        return {
            title: <span className="font-bold text-lg">{t(resource)}</span>,
            key: `resource-${resource}`,
            children: children,
            checkable: false,
        };
    });
};


/**
 * ฟังก์ชันสำหรับ Filter และจัดกลุ่มข้อมูลเข้า Tree ประเภท selectable
 *
 * @param {Array} permissionData - permissions ที่จะถูกกรองและจัดกลุ่ม
 * @param {any} t - ฟังก์ชันสำหรับแปลข้อความ (เช่น i18n.t) เพื่อแปลคำอธิบายและชื่อ Resource
 * @returns {Array} - ข้อมูลที่ถูกจัดกลุ่มและแปลงเป็นรูปแบบที่ Tree ของ Ant Design ต้องการ
 */

export const getSelectableTreeData = (permissionData, t) => {
    const HIDDEN_PERMISSIONS = ["manage_invoices_view", "btn_setFee_bankAccount_action"];
    const isViewAllowed = typeof CheckAdminType === "function" ? CheckAdminType("setTransFee") : true;

    if (!Array.isArray(permissionData)) return [];

    // ดึง Resource ที่มีอยู่ทั้งหมดออกมา (เฉพาะที่เป็น public)
    const resources = [...new Set(permissionData
        .filter(item => item.ispublic === true)
        .map(item => item.resource))];

    return resources.map(resource => {
        const items = permissionData.filter(item => item.resource === resource && item.ispublic === true);

        // แยกรายการปกติ และ รายการที่เป็น Action
        const normalItems = items.filter(item => item.action !== "action");
        const actionItems = items.filter(item => item.action === "action");

        const children = [];

        // เพิ่มรายการปกติเข้าไปในลูกของ Resource
        normalItems.forEach(item => {
            children.push({
                title: t(item.description) || item.name,
                key: parseInt(item.id),
                style: { fontSize: 12 },
            });
        });

        // ถ้ามีการกระทำ (Action) ให้สร้าง Node ย่อย "การกระทำ"
        if (actionItems.length > 0) {
            const actionChildren = actionItems
                .filter(item => isViewAllowed || !HIDDEN_PERMISSIONS.includes(item.name))
                .map(item => ({
                    title: t(item.description) || item.name,
                    key: parseInt(item.id),
                    style: { fontSize: 12 },
                }));

            if (actionChildren.length > 0) {
                children.push({
                    title: <span className="font-bold text-blue-600">{t("action")}</span>,
                    key: `action-group-${resource}`,
                    checkable: false,
                    children: actionChildren,

                });
            }
        }

        return {
            title: <span className="font-bold text-lg">{t(resource)}</span>,
            key: `resource-${resource}`,
            children: children,
            checkable: false,
        };
    });
}